import axios from "axios";
import { tokenStore } from "./tokenStore";

// Isolated axios instance for the refresh call itself. Using a dedicated
// instance (instead of the global `axios`) guarantees this request never
// picks up the refresh interceptor — a 401/403 on /auth/refresh must fail
// fast so the interceptor can log the user out, not loop back into refresh.
const refreshClient = axios.create();

// ─── AES-CBC Encryption Helpers ─────────────────────────────────────────────
// The backend expects credentials encrypted with AES-CBC and a timestamp,
// and will reject any request whose timestamp is older than ~1 minute.
// This prevents replay attacks even if a refresh call is captured.

const hexToArrayBuffer = (hex: string): ArrayBuffer => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes.buffer;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const encryptCredentials = async (payload: unknown): Promise<string> => {
  console.log(payload,"payload")

  // Web Crypto (`crypto.subtle`) is only available in SECURE CONTEXTS:
  //   - https:// pages
  //   - http://localhost / http://127.0.0.1
  // Over plain http:// on any other host, `crypto.subtle` is undefined and
  // the refresh call will crash. Detect that up front with a clear error.
  const subtle = (globalThis.crypto || (window as any).crypto)?.subtle;
  if (!subtle) {
    throw new Error(
      "Web Crypto (crypto.subtle) is unavailable. The app must be served " +
        "over https:// or localhost for token refresh encryption to work. " +
        `Current origin: ${window.location.origin}`
    );
  }

  const keyBuffer = hexToArrayBuffer(import.meta.env.VITE_CREDENTIAL_SECRET_KEY as string);
  const iv = new Uint8Array(
    hexToArrayBuffer(import.meta.env.VITE_CREDENTIAL_IV as string)
  ).slice(0, 16);
console.log(crypto,"crypto",import.meta.env.VITE_CREDENTIAL_IV)
  const cryptoKey = await subtle.importKey(
    "raw",
    keyBuffer,
    { name: "AES-CBC" },
    false,
    ["encrypt"]
  );

  const encoded = new TextEncoder().encode(JSON.stringify(payload));
  const encryptedBuffer = await subtle.encrypt(
    { name: "AES-CBC", iv },
    cryptoKey,
    encoded
  );

  return arrayBufferToBase64(encryptedBuffer);
};

// Server-issued time + single-use nonce. Replaces client-side Date.now() so
// the backend can enforce a tight liveness window AND reject any replayed
// refresh body — the captured `nonce` is consumed atomically on first use.
const fetchServerTimeAndNonce = async (
  basicAuth: string
): Promise<{ serverTime: number; nonce: string }> => {
  const response = await refreshClient.get(
    `${import.meta.env.VITE_API_REFRESH}/auth/server-time-and-nonce`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: basicAuth,
      },
    }
  );
  return response.data;
};

// ─── Refresh Token API Call ─────────────────────────────────────────────────
// Calls the backend refresh endpoint with:
//   - Basic auth (service credentials, identifies the client)
//   - Encrypted server-issued timestamp + nonce (replay protection)
//   - The refresh token in the body (pulled from in-memory store)
//
// On success, updates the in-memory token store with the new tokens and
// returns them so callers can act on them.

export interface RefreshResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export const refreshAccessToken = async (): Promise<RefreshResult> => {
  // Read the current refresh token from memory if we have one. On the very
  // first login this will be null — the backend identifies the session via
  // Basic auth + encrypted timestamp, and returns the first refresh token
  // in the response body.
  const currentRefreshToken = tokenStore.getRefreshToken();
  console.log(currentRefreshToken,"currentRefreshToken")
  const refreshTokenExtract = currentRefreshToken?.split('; ')
    .find(row => row.startsWith('refreshToken='))
    ?.split('=')[1];

  const basicAuth =
    "Basic " +
    btoa(
      `${import.meta.env.VITE_AUTH_USER_USERNAME}:${import.meta.env.VITE_AUTH_USER_PASSWORD}`
    );

  // Pull server time + nonce first; the backend rejects refresh requests
  // whose timestamp drifts outside the allowed window or whose nonce is
  // missing / already consumed.
  const { serverTime, nonce } = await fetchServerTimeAndNonce(basicAuth);
  const credentials = await encryptCredentials({ timestamp: serverTime, nonce });

  const response = await refreshClient.post(
    `${import.meta.env.VITE_API_REFRESH}/auth/refresh`,
    {
      credentials,
      refreshToken: refreshTokenExtract,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: basicAuth,
      },
      // Always send cookies. The backend issues an httpOnly refresh cookie
      // on the parent domain (e.g. `.medvol.in`) on successful refresh; that
      // cookie is what carries the session across page reloads and new tabs.
      // When in-memory state is empty (after reload, or in a freshly opened
      // tab) the cookie is the *only* thing proving the session is alive.
      // CORS on /auth/refresh must allow credentials and echo a specific
      // Origin (not `*`).
      withCredentials: true,
    }
  );
  console.log(response,"response") 
  const { accessToken, idToken, refreshToken: newRefreshToken } = response.data;

  // Persist the freshly minted tokens into the in-memory store so subsequent
  // requests (and the next refresh cycle) use them.
  tokenStore.setTokens({
    accessToken,
    idToken,
    refreshToken: newRefreshToken,
  });

  return { accessToken, idToken, refreshToken: newRefreshToken };
};
